import { AnimatePresence, motion } from "framer-motion";
import { Loader2 } from "lucide-react";
import moment from "moment";
import { useEffect, useMemo, useRef, useState } from "react";

import { BasicTooltip } from "@/components/ui/Tooltip";
import { cn } from "@/lib/utils";

const DEFAULT_MINIMUM_SAVING_VISIBLE_MS = 650;

type SaveStatusVariant = "toolbar" | "dialog";
type SaveStatusIdleBehavior = "show-fallback" | "hide";
type SaveStatusSavingDisplay = "full" | "icon";

interface SaveStatusProps {
  isSaving: boolean;
  savedAt?: Date | string | number | null;
  updatedAt?: Date | string | number | null;
  variant?: SaveStatusVariant;
  minimumSavingVisibleMs?: number;
  idleBehavior?: SaveStatusIdleBehavior;
  savingDisplay?: SaveStatusSavingDisplay;
  ariaLabel?: string;
  className?: string;
}

const formatSavedAt = (savedAt?: Date | string | number | null) => {
  if (savedAt === null || savedAt === undefined) {
    return null;
  }

  const parsedDate = moment(savedAt);
  if (!parsedDate.isValid()) {
    return null;
  }

  return {
    time: parsedDate.format("h:mm:ss A"),
    relative: parsedDate.fromNow(),
    absolute: parsedDate.format("MMM D, YYYY h:mm A"),
    iso: parsedDate.toISOString(),
  };
};

const stateTransition = {
  duration: 0.18,
  ease: [0.22, 1, 0.36, 1],
} as const;

type FormattedSavedAt = NonNullable<ReturnType<typeof formatSavedAt>>;

const contentByVariant = {
  toolbar: {
    savedPrefix: "Last saved at",
    savedFallback: "Not saved yet",
    savedValue: (formatted: FormattedSavedAt) => formatted.time,
  },
  dialog: {
    savedPrefix: "Saved",
    savedFallback: "Not saved yet",
    savedValue: (formatted: FormattedSavedAt) => formatted.relative,
  },
} satisfies Record<
  SaveStatusVariant,
  {
    savedPrefix: string;
    savedFallback: string;
    savedValue: (formatted: FormattedSavedAt) => string;
  }
>;

const useMinimumVisibleDuration = (isVisible: boolean, minimumDurationMs: number) => {
  const [shouldShow, setShouldShow] = useState(isVisible);
  const shouldShowRef = useRef(isVisible);
  const visibleSinceRef = useRef<number | null>(isVisible ? Date.now() : null);
  const timeoutRef = useRef<number | null>(null);

  useEffect(() => {
    shouldShowRef.current = shouldShow;
  }, [shouldShow]);

  useEffect(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }

    if (isVisible) {
      visibleSinceRef.current = Date.now();
      if (!shouldShowRef.current) {
        setShouldShow(true);
      }
      return;
    }

    if (!shouldShowRef.current) {
      return;
    }

    const visibleSince = visibleSinceRef.current;
    if (!visibleSince) {
      setShouldShow(false);
      return;
    }

    const elapsed = Date.now() - visibleSince;
    const remaining = minimumDurationMs - elapsed;

    if (remaining <= 0) {
      setShouldShow(false);
      visibleSinceRef.current = null;
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      setShouldShow(false);
      visibleSinceRef.current = null;
      timeoutRef.current = null;
    }, remaining);

    return () => {
      if (timeoutRef.current !== null) {
        window.clearTimeout(timeoutRef.current);
        timeoutRef.current = null;
      }
    };
  }, [isVisible, minimumDurationMs]);

  return shouldShow;
};

export default function SaveStatus({
  isSaving,
  savedAt,
  updatedAt,
  variant = "toolbar",
  minimumSavingVisibleMs = DEFAULT_MINIMUM_SAVING_VISIBLE_MS,
  idleBehavior = "show-fallback",
  savingDisplay = "full",
  ariaLabel,
  className,
}: SaveStatusProps) {
  const shouldShowSaving = useMinimumVisibleDuration(isSaving, minimumSavingVisibleMs);
  const formattedSavedAt = useMemo(() => formatSavedAt(savedAt ?? updatedAt), [savedAt, updatedAt]);

  if (!shouldShowSaving && !formattedSavedAt && idleBehavior === "hide") {
    return null;
  }

  const copy = contentByVariant[variant];

  let stateKey = "empty";
  if (shouldShowSaving) {
    stateKey = savingDisplay === "icon" ? "saving-icon" : "saving";
  } else if (formattedSavedAt) {
    stateKey = "saved";
  }

  let content = (
    <>
      <span className="font-medium leading-none text-foreground/80">Saved</span>
      <span className="truncate leading-none text-muted-foreground">{copy.savedFallback}</span>
    </>
  );

  if (shouldShowSaving) {
    content =
      savingDisplay === "icon" ? (
        <Loader2 className="block h-3.5 w-3.5 flex-shrink-0 animate-spin" aria-hidden="true" />
      ) : (
        <>
          <Loader2 className="block h-3.5 w-3.5 flex-shrink-0 animate-spin" aria-hidden="true" />
          <span className="font-medium leading-none text-foreground/80">Saving…</span>
        </>
      );
  } else if (formattedSavedAt) {
    content = (
      <>
        <span className="font-medium leading-none text-foreground/80">Saved</span>
        <BasicTooltip content={formattedSavedAt.absolute} side="bottom" delayDuration={400}>
          <time
            dateTime={formattedSavedAt.iso}
            className="truncate leading-none text-muted-foreground"
            aria-label={`Saved ${formattedSavedAt.absolute}`}
          >
            {copy.savedPrefix} {copy.savedValue(formattedSavedAt)}
          </time>
        </BasicTooltip>
      </>
    );
  }

  return (
    <span
      role="status"
      aria-live="polite"
      aria-atomic="true"
      aria-label={ariaLabel}
      className={cn(
        "inline-flex h-4 min-w-0 items-center gap-2 align-middle text-xs leading-none text-muted-foreground",
        className,
      )}
    >
      <AnimatePresence mode="wait" initial={false}>
        <motion.span
          key={stateKey}
          initial={{ opacity: 0, y: 3, filter: "blur(2px)" }}
          animate={{ opacity: 1, y: 0, filter: "blur(0px)" }}
          exit={{ opacity: 0, y: -3, filter: "blur(2px)" }}
          transition={stateTransition}
          className={cn(
            "inline-flex h-4 min-w-0 items-center leading-none",
            savingDisplay === "icon" ? "gap-0" : "gap-2",
          )}
        >
          {content}
        </motion.span>
      </AnimatePresence>
    </span>
  );
}
